import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { LayoutDashboardIcon, UsersIcon, FileTextIcon, BuildingIcon, BriefcaseIcon, CreditCardIcon, SettingsIcon, BarChart2Icon, BellIcon, LogOutIcon, HelpCircleIcon, AlertCircleIcon } from 'lucide-react';
interface SidebarItemProps {
  icon: React.ReactNode;
  title: string;
  to: string;
  active: boolean;
  requiredPermission?: string;
}
const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  title,
  to,
  active,
  requiredPermission
}) => {
  const {
    hasPermission
  } = useAdminAuth();
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null;
  }
  return <Link to={to} className={`flex items-center px-4 py-3 text-sm transition-colors ${active ? 'bg-green-700 text-white' : 'text-green-100 hover:bg-green-700 hover:text-white'}`} data-id="element-135">
      <span className="mr-3" data-id="element-136">{icon}</span>
      <span data-id="element-137">{title}</span>
    </Link>;
};
const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const {
    admin,
    logout,
    isSuperAdmin,
    getMdaName
  } = useAdminAuth();
  // Get appropriate title based on admin role
  const getAdminTitle = () => {
    if (isSuperAdmin()) {
      return 'Super Admin';
    } else {
      return getMdaName() || 'Admin';
    }
  };
  return <div className="bg-green-800 text-white w-64 flex flex-col h-full" data-id="element-138">
      <div className="p-4 border-b border-green-700 flex items-center space-x-3" data-id="element-139">
        <div className="bg-white p-1 rounded-md flex items-center justify-center" data-id="element-140">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-8 w-8" data-id="element-141" />
        </div>
        <div data-id="element-142">
          <h2 className="text-sm font-semibold" data-id="element-143">{getAdminTitle()}</h2>
          <p className="text-xs text-green-200" data-id="element-144">Edo State Services</p>
        </div>
      </div>
      <div className="p-4 border-b border-green-700" data-id="element-145">
        <div className="flex items-center space-x-3" data-id="element-146">
          <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold" data-id="element-147">
            {admin?.name.charAt(0)}
          </div>
          <div data-id="element-148">
            <p className="text-sm font-medium" data-id="element-149">{admin?.name}</p>
            <p className="text-xs text-green-200 capitalize" data-id="element-150">
              {admin?.role === 'mda_admin' ? `${getMdaName()} Admin` : admin?.role.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-2" data-id="element-151">
        <SidebarItem icon={<LayoutDashboardIcon size={20} data-id="element-153" />} title="Dashboard" to="/admin/dashboard" active={location.pathname === '/admin/dashboard'} data-id="element-152" />
        {/* User Management - Super Admin only */}
        {isSuperAdmin() && <SidebarItem icon={<UsersIcon size={20} data-id="element-155" />} title="User Management" to="/admin/users" active={location.pathname.startsWith('/admin/users')} requiredPermission="users:read" data-id="element-154" />}
        <SidebarItem icon={<FileTextIcon size={20} data-id="element-157" />} title="Applications" to="/admin/applications" active={location.pathname.startsWith('/admin/applications')} requiredPermission="applications:read" data-id="element-156" />
        {/* MDA Management - Super Admin only */}
        {isSuperAdmin() && <SidebarItem icon={<BuildingIcon size={20} data-id="element-159" />} title="MDAs" to="/admin/mdas" active={location.pathname.startsWith('/admin/mdas')} requiredPermission="mdas:read" data-id="element-158" />}
        <SidebarItem icon={<BriefcaseIcon size={20} data-id="element-161" />} title="Services" to="/admin/services" active={location.pathname.startsWith('/admin/services')} requiredPermission="services:read" data-id="element-160" />
        <SidebarItem icon={<CreditCardIcon size={20} data-id="element-163" />} title="Payments" to="/admin/payments" active={location.pathname.startsWith('/admin/payments')} requiredPermission="payments:read" data-id="element-162" />
        <SidebarItem icon={<BarChart2Icon size={20} data-id="element-165" />} title="Reports & Analytics" to="/admin/reports" active={location.pathname.startsWith('/admin/reports')} requiredPermission="reports:read" data-id="element-164" />
        <SidebarItem icon={<BellIcon size={20} data-id="element-167" />} title="Notifications" to="/admin/notifications" active={location.pathname.startsWith('/admin/notifications')} data-id="element-166" />
        {/* Settings - Super Admin only */}
        {isSuperAdmin() && <SidebarItem icon={<SettingsIcon size={20} data-id="element-169" />} title="Settings" to="/admin/settings" active={location.pathname.startsWith('/admin/settings')} requiredPermission="settings:read" data-id="element-168" />}
        <SidebarItem icon={<HelpCircleIcon size={20} data-id="element-171" />} title="Help & Support" to="/admin/support" active={location.pathname.startsWith('/admin/support')} data-id="element-170" />
      </nav>
      <div className="p-4 border-t border-green-700" data-id="element-172">
        <button onClick={logout} className="flex items-center w-full px-4 py-2 text-sm text-green-100 hover:bg-green-700 hover:text-white rounded-md transition-colors" data-id="element-173">
          <LogOutIcon size={20} className="mr-3" data-id="element-174" />
          Logout
        </button>
      </div>
    </div>;
};
export default AdminSidebar;